import { A, Container, H1, H2, H3, Ol, P, Strong, Ul } from '#app/ui/components'

export default function RegistrationConditions() {
	return (
		<Container className="py-16">
			<div className="mx-auto max-w-3xl space-y-8">
				<H1>Registration and User Account Conditions</H1>

				<Ol>
					<li className="space-y-4">
						<H2>Identification and Contact</H2>
						<P>
							The web platform{` `}
							<A
								href="https://welovenight.com/"
								target="_blank"
								rel="noopener noreferrer"
							>
								https://welovenight.com/
							</A>{` `}
							(hereinafter, the "Platform") is owned by WE LOVE NIGHT S.L., with
							Tax ID B75524363, with registered office at Calle Miquel Marqués
							25, Palma de Mallorca (Illes Balears), 07005, Spain, and its
							contact email address is bielmartinez@welovenight.com
							(hereinafter, "WELOVENIGHT" or the "Company").
						</P>
						<P>
							For any enquiry, complaint or request for information, users may
							contact WELOVENIGHT through:
						</P>
						<div className="space-y-2">
							<P className="mb-0">
								<Strong>Email:</Strong>{` `}
								<A href="mailto:bielmartinez@welovenight.com">
									bielmartinez@welovenight.com
								</A>
							</P>
							<P className="mb-0">
								<Strong>Telephone:</Strong> +34 628 38 60 38
							</P>
							<P>
								<Strong>Postal address:</Strong> Calle Miquel Marqués, 25.
								07005, Palma de Mallorca (Illes Balears), Spain
							</P>
						</div>
					</li>

					<li className="space-y-4">
						<H2>Purpose and Scope</H2>
						<P>
							These Registration and User Account Conditions (hereinafter, the
							"Registration Conditions") establish the conditions governing user
							registration and account management on the Platform. Together with
							the <A href="/purchase-conditions">Ticket Purchase Conditions</A>,{` `}
							<A href="/legal-notice">Legal Notice</A>,{` `}
							<A href="/privacy-policy">Privacy Policy</A> and Cookie Policy, they form
							the applicable legal framework (hereinafter, the "Legal Terms of
							the Platform") for the use of the Platform.
						</P>
						<P>
							The use of the Platform and the creation of a user account
							attribute to the person doing so the status of User (hereinafter,
							"User"). Acceptance of these Registration Conditions by the User
							shall be an essential requirement for creating an account and
							accessing the Platform's functionalities.
						</P>
						<P>
							WELOVENIGHT provides Users with an advance digital ticket sales
							service for leisure and nightlife events, including nightclubs,
							afternoon parties, themed parties and other similar events
							(hereinafter, the "Events").
						</P>
						<P>
							These Registration Conditions will be updated periodically,
							therefore those published when the User registers or uses the
							Platform will apply.
						</P>
					</li>

					<li className="space-y-4">
						<H2>User Registration and Verification</H2>

						<Ol>
							<li className="space-y-4">
								<H3>Purchase methods and account requirements</H3>
								<P>Users may purchase tickets through different methods:</P>
								<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
									<li>
										<P>
											<Strong>Purchase with registered account:</Strong> Have a
											registered account on the Platform and be logged into
											their account.
										</P>
									</li>
									<li>
										<P>
											<Strong>Purchase without registered account:</Strong>{` `}
											Provide a valid email address for ticket delivery.
										</P>
									</li>
								</Ol>
								<P>
									<Strong>Ticket association with accounts:</Strong>
								</P>
								<P>
									If the email address used for purchase corresponds to an
									existing registered account, the ticket will be automatically
									associated with that account.
								</P>
								<P>
									If the Customer creates an account after purchase using the
									same email address, the ticket will be automatically
									associated with the newly created account.
								</P>
								<P>
									If no account is created, the ticket will be just delivered
									via email with a QR code for Event access. Tickets delivered
									via email function identically to tickets associated with
									accounts.
								</P>
							</li>

							<li className="space-y-4">
								<H3>Universal access conditions</H3>
								<P>
									Access to the Platform is open to any natural person of legal
									age who wishes to register as a User, without prejudice to the
									specific conditions established for each type of role and
									WELOVENIGHT's powers to deny or suspend accounts in accordance
									with these Registration Conditions.
								</P>
								<P>
									Registration on the Platform is free and voluntary. While
									registration is not required for purchasing tickets, it is
									mandatory to create events or perform management functions as
									an Organiser or Public Relations.
								</P>
							</li>

							<li className="space-y-4">
								<H3>Age of majority requirement</H3>
								<P>
									By registering on the Platform, the User declares that the
									User is over 18 years of age and has legal capacity to enter
									into contracts.
								</P>
								<P>
									All Users must be of legal age (18 years or older) to create
									an account on the Platform, regardless of the role they will
									perform (Buyer, Organiser, Public Relations, etc.).
								</P>
								<P>
									Users under 18 years of age may not create accounts, make
									purchases or register for Events, unless the Users have
									express authorisation from the Users' parents or legal
									guardians. The Company reserves the right to request proof of
									age at any time.
								</P>
							</li>

							<li className="space-y-4">
								<H3>Registration process</H3>
								<P>
									The account creation process is the same for all types of
									users. To register on the Platform, the User must complete the
									registration form providing the following mandatory
									information:
								</P>
								<Ul className="ml-4 list-disc space-y-1">
									<li>
										<P>Full name</P>
									</li>
									<li>
										<P>Email address</P>
									</li>
									<li>
										<P>Telephone number</P>
									</li>
									<li>
										<P>Date of birth (to verify age of majority)</P>
									</li>
									<li>
										<P>Gender</P>
									</li>
									<li>
										<P>Secure password</P>
									</li>
								</Ul>
								<P>
									By completing the registration form and clicking the "Submit"
									button, the User confirms that the User has read, understood
									and accepts these Registration Conditions, as well as the{` `}
									<A href="/legal-notice">Legal Notice</A>,{` `}
									<A href="/privacy-policy">Privacy Policy</A> and Cookie Policy.
								</P>
							</li>

							<li className="space-y-4">
								<H3>Identity verification procedures</H3>
								<P>
									The Company reserves the right to request identity
									verification from any User at any time, especially when:
								</P>
								<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
									<li>
										<P>
											Suspicious or fraudulent activities are detected in the
											account.
										</P>
									</li>
									<li>
										<P>
											The User requests to perform functions as an Organiser or
											Public Relations.
										</P>
									</li>
									<li>
										<P>
											Claims, complaints or incidents related to the account
											occur.
										</P>
									</li>
									<li>
										<P>
											It is necessary to comply with legal obligations or
											requirements from competent authorities.
										</P>
									</li>
									<li>
										<P>
											The volume of transactions or the nature of operations so
											advises.
										</P>
									</li>
									<li>
										<P>The User appears to be under the age of majority.</P>
									</li>
								</Ol>
								<P>
									The Company may request additional or supplementary
									documentation if deemed necessary. The documentation provided
									will be processed in accordance with the{` `}
									<A href="/privacy-policy">Privacy Policy</A> and current regulations
									on personal data protection.
								</P>
								<P>
									The Company may suspend account access until identity
									verification is satisfactorily completed.
								</P>
							</li>

							<li className="space-y-4">
								<H3>Validation of personal data</H3>
								<P>
									The Company may implement automatic or manual validation
									mechanisms for personal data provided by Users during the
									registration process, including without limitation:
								</P>
								<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
									<li>
										<P>
											Verification of email validity by sending a confirmation
											link.
										</P>
									</li>
									<li>
										<P>
											Validation of telephone number by SMS code or telephone
											call.
										</P>
									</li>
									<li>
										<P>Verification of the consistency of the data provided.</P>
									</li>
									<li>
										<P>Contrast with authorised databases.</P>
									</li>
									<li>
										<P>
											Documentary verification of identity in accordance with
											the previous section.
										</P>
									</li>
								</Ol>
								<P>
									The User undertakes to provide only truthful, accurate,
									complete and up-to-date data, being responsible for keeping
									such information duly updated. Providing false, inaccurate or
									third-party data constitutes serious breach of these
									Registration Conditions. The Company reserves the right to
									verify the information provided and to suspend or cancel
									accounts containing false or fraudulent data.
								</P>
								<P>
									Providing false information about age, identity or any other
									mandatory data may result in immediate account cancellation
									and may constitute a criminal offence.
								</P>
							</li>

							<li className="space-y-4">
								<H3>User obligations upon registration</H3>
								<P>
									By proceeding with registration on the Platform, the User
									expressly undertakes to:
								</P>
								<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
									<li>
										<P>
											Provide truthful, accurate, complete and up-to-date
											information in all mandatory fields of the registration
											form.
										</P>
									</li>
									<li>
										<P>
											Be of legal age (18 years or older) at the time of
											registration.
										</P>
									</li>
									<li>
										<P>Maintain their data updated.</P>
									</li>
									<li>
										<P>
											Diligently safeguard their access credentials (username
											and password), keeping them secret and not sharing them
											with third parties.
										</P>
									</li>
									<li>
										<P>
											Use a secure password containing a combination of letters,
											numbers and special characters.
										</P>
									</li>
									<li>
										<P>
											Immediately notify the Company of any unauthorised use of
											the User's account or any other security breach of which
											the User becomes aware.
										</P>
									</li>
									<li>
										<P>
											Not create more than one User account per natural person,
											except with express authorisation from the Company.
										</P>
									</li>
									<li>
										<P>
											Not use false identities or impersonate third parties.
										</P>
									</li>
									<li>
										<P>
											Not register accounts for illicit, fraudulent purposes or
											contrary to these Registration Conditions.
										</P>
									</li>
									<li>
										<P>
											Not assign, transfer or allow the use of their account to
											third parties without prior express authorisation from the
											Company.
										</P>
									</li>
									<li>
										<P>
											Use the Platform in accordance with current legislation,
											morality, public order and good customs.
										</P>
									</li>
									<li>
										<P>
											Respect the intellectual and industrial property rights of
											the Company and third parties.
										</P>
									</li>
									<li>
										<P>
											Not use the Platform to send spam, malware or any other
											harmful content.
										</P>
									</li>
									<li>
										<P>
											Not attempt to gain unauthorised access to other users'
											accounts or to the Platform's systems.
										</P>
									</li>
								</Ol>
							</li>

							<li className="space-y-4">
								<H3>Account security</H3>
								<P>
									The User is responsible for maintaining the confidentiality of
									the User's access credentials (username and password) and all
									activities carried out from the User's account. The User
									undertakes to immediately notify the Company of any
									unauthorised use of the User's account.
								</P>
								<P>The Company recommends:</P>
								<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
									<li>
										<P>
											Using a unique password not used on other websites or
											services.
										</P>
									</li>
									<li>
										<P>
											Changing the password periodically and not sharing the
											password with third parties.
										</P>
									</li>
									<li>
										<P>
											Logging out of the account when using shared or public
											devices.
										</P>
									</li>
									<li>
										<P>Enabling two-factor authentication if available.</P>
									</li>
								</Ol>
								<P>
									The Company will never request the User's password by email,
									telephone or any other means. Any such request should be
									considered fraudulent and reported immediately by the User.
								</P>
							</li>

							<li className="space-y-4">
								<H3>Grounds for denial or suspension of accounts</H3>
								<P>
									The Company reserves the right to deny registration,
									temporarily or permanently suspend, or cancel any User
									account, without prior notice and without right to
									compensation, when any of the following circumstances occur:
								</P>
								<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
									<li>
										<P>
											Breach of any of the obligations established in these
											Registration Conditions or in the rest of the Legal Terms
											of the Platform.
										</P>
									</li>
									<li>
										<P>
											Provision of false, inaccurate, incomplete or third-party
											data during the registration process or at any subsequent
											time.
										</P>
									</li>
									<li>
										<P>
											Detection of fraudulent, suspicious or unlawful
											activities, including without limitation: mass purchase of
											tickets for illegal resale, use of stolen or cloned credit
											cards, identity theft, creation of multiple accounts to
											evade restrictions, age falsification.
										</P>
									</li>
									<li>
										<P>
											Use of the account for purposes other than those provided
											for in these Registration Conditions.
										</P>
									</li>
									<li>
										<P>
											Conduct that may damage the image, reputation or
											legitimate interests of WELOVENIGHT, Event organisers,
											other Users or third parties.
										</P>
									</li>
									<li>
										<P>Repeated breach of Events rules or admission rights.</P>
									</li>
									<li>
										<P>Making fraudulent or abusive claims or refunds.</P>
									</li>
									<li>
										<P>
											Assignment, transfer or shared use of the account without
											authorisation.
										</P>
									</li>
									<li>
										<P>
											Prolonged account inactivity exceeding twenty-four (24)
											consecutive months, after notifying the User thirty (30)
											days in advance.
										</P>
									</li>
									<li>
										<P>
											Request from competent authorities or judicial or
											administrative resolution.
										</P>
									</li>
									<li>
										<P>
											Breach of security measures or attempts to compromise the
											Platform's integrity.
										</P>
									</li>
									<li>
										<P>
											Use of automated systems (bots, scripts) to create
											accounts or interact with the Platform without
											authorisation.
										</P>
									</li>
									<li>
										<P>
											Any other justified cause that, at WELOVENIGHT's
											reasonable discretion, advises denial, suspension or
											cancellation of the account.
										</P>
									</li>
								</Ol>
								<P>
									In case of temporary suspension, the Company will communicate
									to the User, whenever possible, the reasons for suspension and
									the conditions for restoring the account. The User will have
									the right to submit allegations within seven (7) days from
									notification.
								</P>
								<P>
									In case of permanent cancellation, the User will lose all
									rights associated with their account, including history,
									loyalty points and any other accumulated benefits, without
									prejudice to the right to obtain tickets already purchased and
									paid for future Events.
								</P>
							</li>
						</Ol>
					</li>

					<li className="space-y-4">
						<H2>User Types, Roles and Hierarchical System</H2>
						<P>
							The Platform establishes a hierarchical user system with different
							access levels, functionalities and responsibilities. Access to
							special roles is governed by an invitation system ensuring
							traceability and control.
						</P>

						<Ol>
							<li className="space-y-4">
								<H3>Role Hierarchy</H3>
								<P>
									The Platform contemplates the following roles in descending
									hierarchical order:
								</P>
								<Ol>
									<li>
										<P>
											<Strong>Owner/Administrator</Strong>
										</P>
										<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
											<li>
												<P>Highest authority with complete Platform control.</P>
											</li>
											<li>
												<P>
													Unlimited access to all functionalities, statistics
													and data.
												</P>
											</li>
											<li>
												<P>
													Full management of users, events, system
													configurations and general parameters.
												</P>
											</li>
											<li>
												<P>
													Establishment of commissions, refund policies and
													economic conditions.
												</P>
											</li>
											<li>
												<P>Suspension or cancellation of any user account.</P>
											</li>
										</Ol>
									</li>
									<li>
										<P>
											<Strong>Event Organiser</Strong>
										</P>
										<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
											<li>
												<P>
													Creation and management of own events with full
													configuration of Events parameters (date, location,
													capacity, prices, specific conditions). Among his
													management obligations is the duty to verify the age
													of attendees and to accept or reject attendees
													according to their policies and in accordance with the
													applicable regulation.
												</P>
											</li>
											<li>
												<P>
													Invitation and management of Manager Chiefs and Public
													Relations linked to their Events.
												</P>
											</li>
											<li>
												<P>
													Access to sales and attendance statistics, management
													of affiliate links, commissions, refunds and claims.
												</P>
											</li>
											<li>
												<P>
													Limitations: Cannot access other Event Organisers'
													Events or data, modify general Platform parameters or
													suspend user accounts. Subject to commissions
													established by the Company.
												</P>
											</li>
										</Ol>
									</li>
									<li>
										<P>
											<Strong>
												Manager Chief (Public Relations Coordinator)
											</Strong>
										</P>
										<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
											<li>
												<P>
													Coordination and supervision of Public Relations
													teams.
												</P>
											</li>
											<li>
												<P>
													Invitation and management of PRs, access to aggregated
													statistics and distribution of affiliate links.
												</P>
											</li>
											<li>
												<P>
													Proposal of commissions subject to Manager approval.
												</P>
											</li>
											<li>
												<P>
													Limitations: Cannot create or modify Events, establish
													prices or access complete financial data.
													Hierarchically dependent on the inviting Customer.
												</P>
											</li>
										</Ol>
									</li>
									<li>
										<P>
											<Strong>Public Relations (PR)</Strong>
										</P>
										<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
											<li>
												<P>
													Promotion of events through personalised affiliate
													links with access to own statistics (clicks,
													registrations, sales and commissions).
												</P>
											</li>
											<li>
												<P>
													Limitations: Cannot create, modify or delete Events,
													access other PRs' data, modify prices or validate
													tickets. Can only promote authorised Events. Subject
													to commission conditions established by the Organiser.
												</P>
											</li>
										</Ol>
									</li>
									<li>
										<P>
											<Strong>Customer</Strong>
										</P>
										<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
											<li>
												<P>
													Browsing and purchasing tickets for Events, access to
													purchase history and personal data management.
												</P>
											</li>
											<li>
												<P>Receipt of notifications.</P>
											</li>
											<li>
												<P>
													Limitations: Cannot create events, access other users'
													data or generate affiliate links. Subject to purchase
													limits established by Organisers.
												</P>
											</li>
										</Ol>
									</li>
								</Ol>
							</li>

							<li className="space-y-4">
								<H3>Hierarchical Invitation System</H3>
								<P>
									Access to Organiser and Public Relations roles requires prior
									invitation by a user with superior permissions.
									Self-assignment of these roles is not possible.
								</P>
								<Ol>
									<li>
										<P>
											<Strong>Requirements for Organiser:</Strong>
										</P>
										<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
											<li>
												<P>Invitation from Platform Administrator (Owner).</P>
											</li>
											<li>
												<P>
													Enhanced identity verification and acceptance of
													specific Customer conditions.
												</P>
											</li>
											<li>
												<P>
													Legal documentation if acting as legal entity
													(articles of incorporation, powers of representation,
													tax registration, administrative licences, civil
													liability insurance).
												</P>
											</li>
										</Ol>
									</li>
									<li>
										<P>
											<Strong>Requirements for Public Relations:</Strong>
										</P>
										<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
											<li>
												<P>
													Invitation from an Organiser or Manager Chief with
													link to specific Organiser.
												</P>
											</li>
											<li>
												<P>
													Acceptance of specific conditions including promotion
													rules, commission system, confidentiality obligations
													and prohibition of fraudulent practices.
												</P>
											</li>
											<li>
												<P>
													<Strong>Validation Process:</Strong> The invitation
													process comprises three phases: (1) Invitation
													issuance with unique link sent by email; (2) Invitee
													accesses link, reviews conditions, accepts invitation
													and completes identity verification if required; (3)
													Role is automatically activated and hierarchical
													relationship is recorded for traceability and
													responsibility purposes.
												</P>
											</li>
										</Ol>
									</li>
								</Ol>
							</li>

							<li className="space-y-4">
								<H3>Permission Delegation</H3>
								<P>
									Users with superior roles may delegate specific permissions to
									users with inferior roles, subject to the following rules:
								</P>
								<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
									<li>
										<P>
											<Strong>Revocability:</Strong> All delegations are
											unilaterally revocable at any time without right to
											compensation.
										</P>
									</li>
									<li>
										<P>
											<Strong>Traceability:</Strong> All delegations are
											recorded identifying delegator, delegate, delegated
											permissions, date, term and reason.
										</P>
									</li>
									<li>
										<P>
											<Strong>Material limits:</Strong> Sub-delegation is
											prohibited without prior authorisation.
										</P>
									</li>
									<li>
										<P>
											The Company may require additional documentation for
											high-level delegations (financial management, access to
											third-party personal data) and may revoke any delegation
											when detecting improper use, security concerns or breach
											of established conditions.
										</P>
									</li>
								</Ol>
							</li>

							<li className="space-y-4">
								<H3>Hierarchical Responsibility</H3>
								<P>
									Each user with superior role assumes responsibility for acts
									carried out by users of inferior roles they have invited or
									who depend hierarchically on them:
								</P>
								<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
									<li>
										<P>
											<Strong>Organiser:</Strong> Responsible for truthfulness
											and lawfulness of Events information, compliance with
											applicable regulations (licences, insurance, public
											entertainment regulations), acts of invited Manager Chief
											and Public Relations in relation to Event promotion, and
											tax obligations arising from events.
										</P>
									</li>
									<li>
										<P>
											<Strong>Manager Chief:</Strong> Responsible for adequate
											supervision of PRs under their charge and promotional
											practices of coordinated PRs when having had knowledge of
											irregularities and not having communicated them.
										</P>
									</li>
									<li>
										<P>
											<Strong>PR:</Strong> Responsible for promotional
											practices, truthfulness of communications, compliance with
											advertising and consumer protection regulations, and
											proper use of affiliate links.
										</P>
									</li>
								</Ol>
								<P>
									In case of damages caused to third parties or to the Company,
									the user with superior role who invited them will be jointly
									and severally liable, without prejudice to their right of
									recovery.
								</P>
							</li>
							<li className="space-y-4">
								<H3>Account Management</H3>
								<Ol>
									<li className="space-y-4">
										<P>
											<Strong>Access to personal data</Strong>
										</P>
										<P>
											Users may access, review and modify their personal data at
											any time through their account settings on the Platform.
											Users have the right to access their personal data,
											rectify inaccurate or incomplete data, request erasure of
											data (right to be forgotten), restrict data processing,
											data portability, object to processing and withdraw
											consent at any time. For more information on data
											protection rights, Users should consult the{` `}
											<A href="/privacy-policy">Privacy Policy</A>.
										</P>
									</li>

									<li className="space-y-4">
										<P>
											<Strong>Account modification</Strong>
										</P>
										<P>
											Users may modify the following information in their
											account: name and surname, email address (subject to
											verification), telephone number (subject to verification),
											password, communication preferences and marketing
											preferences.
										</P>
										<P>
											Users cannot modify account creation date, purchase
											history or role assignments (subject to invitation
											system).
										</P>
									</li>

									<li className="space-y-4">
										<P>
											<Strong>Account deletion</Strong>
										</P>
										<P>
											Users may request permanent deletion of the Users' account
											in the profile settings, under section "Data and Privacy",
											by clicking "Delete Account". Upon deletion:
										</P>
										<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
											<li>
												<P>
													All personal data will be erased in accordance with
													data protection regulations.
												</P>
											</li>
											<li>
												<P>
													Purchase history will be anonymised for legal and
													accounting purposes.
												</P>
											</li>
											<li>
												<P>Tickets for future Events will not be cancelled.</P>
											</li>
											<li>
												<P>The action is irreversible.</P>
											</li>
											<li>
												<P>
													The Company will process deletion requests within
													thirty (30) days.
												</P>
											</li>
										</Ol>
									</li>
								</Ol>
							</li>
						</Ol>
					</li>

					<li className="space-y-4">
						<H2>Processing of Personal Data</H2>
						<P>
							Information on the processing of personal data can be found in the{` `}
							<A href="/privacy-policy">Privacy Policy</A>.
						</P>
					</li>

					<li className="space-y-4">
						<H2>Intellectual and Industrial Property</H2>
						<P>
							The Platform's contents, including texts, images, designs, logos,
							source code and any other element, are owned by the Company or its
							collaborators and are considered protected works in accordance
							with Royal Decree 1/1996, of 12 April, approving the consolidated
							text of the Intellectual Property Law. Users may not:
						</P>
						<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
							<li>
								<P>
									Copy, reproduce or distribute Platform content without
									authorisation.
								</P>
							</li>
							<li>
								<P>Use WELOVENIGHT's trademarks or logos without permission.</P>
							</li>
							<li>
								<P>Reverse engineer or decompile the Platform's software.</P>
							</li>
							<li>
								<P>Create derivative works based on the Platform.</P>
							</li>
						</Ol>
						<P>
							Users grant the Company a non-exclusive, worldwide, royalty-free
							licence to use content the Users upload to the Platform (profile
							pictures, event descriptions etc.) for the purpose of providing
							the service.
						</P>
					</li>

					<li className="space-y-4">
						<H2>Liability</H2>
						<Ol>
							<li className="space-y-4">
								<H3>Company's liability</H3>
								<P>
									The Company acts solely as a digital intermediary, providing a
									platform that facilitates interaction and transactions between
									users and third-party Event Organisers. Unless expressly
									stated otherwise, the Company does not organize, manage, or
									assume responsibility for the execution of any Events listed
									on the platform.
								</P>
								<P>
									The Company's liability for account registration and
									management is limited in accordance with Articles 1102 to 1106
									of the Spanish Civil Code:
								</P>
								<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
									<li>
										<P>
											The Company shall not be liable for damages arising from
											slight negligence.
										</P>
									</li>
									<li>
										<P>
											The Company shall not be liable in cases of force majeure
											or fortuitous event.
										</P>
									</li>
								</Ol>
								<P>
									In addition, the Company shall not be held responsible for any
									disputes, claims, damages, or losses (whether direct,
									indirect, incidental, or consequential) arising from
									interactions, transactions, or contractual relationships
									between users and third parties, including but not limited to
									Event Organisers and other users. Any such claims must be
									addressed directly with the respective third party. The
									Company shall not be responsible for verifying the age of
									attendees at event entrances, except when acting as an Event
									Organiser.
								</P>
								<P>
									To the maximum extent permitted by law, the User agrees to
									hold the Company, its affiliates, directors, employees, and
									agents harmless from any liability, claims or demands arising
									from such third-party disputes.
								</P>
								<P>
									Nothing in this clause shall exclude or limit the Company's
									liability in the following cases:
								</P>
								<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
									<li>
										<P>
											Personal injury or death caused by the Company's
											negligence.
										</P>
									</li>
									<li>
										<P>Fraud or wilful misconduct.</P>
									</li>
									<li>
										<P>Breach of data protection obligations.</P>
									</li>
									<li>
										<P>
											Any other liability that cannot be limited or excluded
											under applicable law.
										</P>
									</li>
								</Ol>
								<P>
									The Company's total liability for any claim directly arising
									from the registration or management of a user account shall in
									any case be limited to one hundred euros (€100), unless
									otherwise required by law.
								</P>
							</li>

							<li className="space-y-4">
								<H3>User liability</H3>
								<P>Users are liable for:</P>
								<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
									<li>
										<P>
											Truthfulness and accuracy of data provided during
											registration.
										</P>
									</li>
									<li>
										<P>
											Proper use of their account and safeguarding of
											credentials.
										</P>
									</li>
									<li>
										<P>Acts carried out from their account.</P>
									</li>
									<li>
										<P>Compliance with these Registration Conditions.</P>
									</li>
									<li>
										<P>
											Damages caused to the Company, other users or third
											parties through breach of these conditions.
										</P>
									</li>
								</Ol>
							</li>
						</Ol>
					</li>

					<li className="space-y-4">
						<H2>Modification of Registration Conditions</H2>
						<P>
							The Company reserves the right to modify these Registration
							Conditions at any time. Modifications will be notified to
							registered Users by:
						</P>
						<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
							<li>
								<P>Email to the registered address, or</P>
							</li>
							<li>
								<P>Prominent notice on the Platform, or</P>
							</li>
							<li>
								<P>Notification upon next login.</P>
							</li>
						</Ol>
						<P>
							Modifications will take effect fifteen (15) days after
							notification, except:
						</P>
						<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
							<li>
								<P>Modifications required by law (immediate effect)</P>
							</li>
							<li>
								<P>Modifications favourable to Users (immediate effect)</P>
							</li>
							<li>
								<P>
									Substantial modifications affecting user rights (thirty days'
									notice)
								</P>
							</li>
						</Ol>
						<P>
							Continued use of the account after the effective date constitutes
							acceptance of the modified conditions. Users who do not accept the
							modifications may delete their account.
						</P>
					</li>

					<li className="space-y-4">
						<H2>Partial Nullity</H2>
						<P>
							If any provision of these Registration Conditions is held to be
							null, invalid or unenforceable, the remaining provisions will
							remain in full force and effect. The null provision will be
							replaced by a valid provision that most closely reflects the
							original intention.
						</P>
					</li>

					<li className="space-y-4">
						<H2>Waiver</H2>
						<P>
							Failure by WELOVENIGHT to enforce any provision of these
							Registration Conditions does not constitute a waiver of that
							provision or any other provision. No waiver will be effective
							unless made in writing.
						</P>
					</li>

					<li className="space-y-4">
						<H2>Entire Agreement</H2>
						<P>
							These Registration Conditions, together with the{` `}
							<A href="/legal-notice">Legal Notice</A>,{` `}
							<A href="/privacy-policy">Privacy Policy</A> and Cookie Policy,
							constitute the entire agreement between the User and the Company
							regarding account registration and management.
						</P>
					</li>

					<li className="space-y-4">
						<H2>Assignment</H2>
						<P>
							Users may not assign or transfer their rights or obligations under
							these Registration Conditions without the Company's prior written
							consent.
						</P>
						<P>
							The Company may assign or transfer its rights and obligations
							under these Registration Conditions at any time, provided such
							assignment does not adversely affect User rights.
						</P>
					</li>

					<li className="space-y-4">
						<H2>Applicable Law and Jurisdiction</H2>

						<Ol>
							<li className="space-y-4">
								<H3>Applicable law</H3>
								<P>
									These Registration Conditions are governed by Spanish law.
								</P>
							</li>

							<li className="space-y-4">
								<H3>Jurisdiction for consumers</H3>
								<P>
									In the event of any dispute, the User that is a consumer will
									first attempt to resolve the matter amicably by contacting the
									Company's customer service team.
								</P>
								<P>
									If no amicable solution is reached, and in accordance with
									applicable consumer protection laws, the consumer may refer
									the dispute to an officially recognized Alternative Dispute
									Resolution (ADR) entity in Spain.
								</P>
								<P>
									Furthermore, pursuant to Article 29 of the General Law for the
									Defence of Consumers and Users, any legal dispute shall be
									submitted to the Courts of the consumer's place of residence.
								</P>
								<P>
									Users can consult the official list of ADR entities in Spain
									on the website of the Spanish Consumer Affairs Authority at{` `}
									<A
										href="https://www.consumo.gob.es"
										target="_blank"
										rel="noopener noreferrer"
									>
										https://www.consumo.gob.es
									</A>
									.
								</P>
							</li>

							<li className="space-y-4">
								<H3>Jurisdiction for professionals</H3>
								<P>
									For Users without consumer status (Organisers, PRs,
									companies), disputes will be submitted to the exclusive
									jurisdiction of the Courts and Tribunals of Palma de Mallorca,
									Spain.
								</P>
							</li>
						</Ol>
					</li>

					<li className="space-y-4">
						<H2>Customer Service</H2>
						<P>
							For any enquiry regarding registration or account management,
							contact:
						</P>
						<div className="space-y-2">
							<P className="mb-0">
								<Strong>Customer Service - WELOVENIGHT</Strong>
							</P>
							<Ul className="ml-4 list-disc space-y-1">
								<li>
									<P className="mb-0">
										<Strong>Email:</Strong>{` `}
										<A href="mailto:bielmartinez@welovenight.com">
											bielmartinez@welovenight.com
										</A>
									</P>
								</li>
								<li>
									<P className="mb-0">
										<Strong>Telephone:</Strong> +34 628 38 60 38
									</P>
								</li>
								<li>
									<P>
										<Strong>Hours:</Strong> 09:00-17:00
									</P>
								</li>
							</Ul>
						</div>
						<P>For data protection enquiries:</P>
						<Ul className="ml-4 list-disc space-y-1">
							<li>
								<P>
									<Strong>Email:</Strong>{` `}
									<A href="mailto:bielmartinez@welovenight.com">
										bielmartinez@welovenight.com
									</A>
								</P>
							</li>
						</Ul>
					</li>

					<li className="space-y-4">
						<H2>Acceptance of Registration Conditions</H2>
						<P>
							By clicking "Submit", the User acknowledges and accepts that the
							User:
						</P>
						<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,decimal)')'] [&>li]:pl-2">
							<li>
								<P>
									Has read and understood these Registration Conditions in full.
								</P>
							</li>
							<li>
								<P>Is of legal age (18 years or older).</P>
							</li>
							<li>
								<P>The data provided is truthful, accurate and complete.</P>
							</li>
							<li>
								<P>
									Accepts the <A href="/legal-notice">Legal Notice</A>,{` `}
									<A href="/privacy-policy">Privacy Policy</A> and Cookie Policy.
								</P>
							</li>
							<li>
								<P>
									Consents to processing of the User's personal data as
									described.
								</P>
							</li>
							<li>
								<P>Understands the User's rights and obligations as a User.</P>
							</li>
							<li>
								<P>
									Accepts the hierarchical structure and invitation system (if
									applicable).
								</P>
							</li>
						</Ol>
					</li>
				</Ol>

				<div className="mt-8 space-y-2 border-t pt-8">
					<P className="mb-0">
						<Strong>Version:</Strong> 1.0
					</P>
					<P className="mb-0">
						<Strong>Effective date:</Strong> October 2025
					</P>
					<P>
						<Strong>Last reviewed:</Strong> October 2025
					</P>
				</div>
			</div>
		</Container>
	)
}
